import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, BookOpen, ClipboardList, Presentation, RefreshCcw } from "lucide-react";
import { Logo } from "@/components/atas/Logo";
import { TipBanner } from "@/components/atas/TipBanner";
import { API } from "@/lib/api";

const rm = (sen) => `RM${(sen / 100).toFixed(2)}`;
const btnMain = "h-12 px-7 rounded-full bg-ink text-bg font-semibold inline-flex items-center gap-2 hover:opacity-90 transition-opacity";
const btnAlt = "h-12 px-7 rounded-full border border-line text-ink inline-flex items-center gap-2 hover:bg-surface transition-colors";

const STEPS = [
  [ClipboardList, "1. Sebelum Program", "Kaji wahyu, bina objektif dan sediakan bahan."],
  [Presentation, "2. Semasa Program", "Gunakan aktiviti, soalan refleksi dan bahan pembelajaran."],
  [RefreshCcw, "3. Selepas Program", "Rekod AAR dan bina versi program yang lebih baik."],
];

const METHODS = [
  ["BDQ", "Berinteraksi Dengan Al-Quran", "BDQ membantu kita memahami ayat al-Quran secara berlapis, melihat hubungan ayat dan surah serta membina kefahaman yang berasaskan dalil."],
  ["SKDA", "Solusi Kehidupan Dalam al-Quran", "SKDA membantu kita mengenal pasti kata kunci, membina konsep, memahami masalah kehidupan dan merangka solusi berasaskan petunjuk al-Quran."],
  ["KBM", "Klinik Bina Modul", "KBM membantu menukar hasil kajian kepada objektif pembelajaran, kandungan modul, aktiviti peserta dan kaedah penilaian yang tersusun."],
  ["ADAB", "Atas · Depan · Belakang (Yasin 36:45)", "ADAB membantu trainer menyusun dan mempersembahkan mesej supaya lebih jelas, teratur dan mudah difahami peserta."],
];

const Hero = () => (
  <section className="pt-14 sm:pt-20 pb-16 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-end">
    <div className="fade-up">
      <Logo size="lg" />
      <div className="eyebrow mt-10">HTE — Heaven To Earth</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] mt-3 text-ink max-w-2xl" data-testid="home-hero-title">
        Dari <span className="text-q">Wahyu</span> kepada Tindakan
      </h1>
      <p className="mt-6 text-base text-mute max-w-xl leading-relaxed">Siapkan kajian, modul, bahan latihan dan penambahbaikan program dalam satu ruang kerja berasaskan al-Quran.</p>
      <div className="flex flex-wrap gap-3 mt-8">
        <Link to="/program" className={btnMain} data-testid="home-cta-program">Saya Ada Program <ArrowRight size={16} /></Link>
        <Link to="/hte" className={btnAlt} data-testid="home-cta-kaji"><BookOpen size={16} /> Kaji Ayat</Link>
      </div>
    </div>
    <div className="hidden lg:flex justify-end fade-up" style={{ animationDelay: "120ms" }}>
      <div className="flex items-stretch gap-4 h-64">
        <span className="w-px descent-line rounded-full" />
        <ul className="flex flex-col justify-between py-1 font-mono-data text-sm">
          {[["Q", "Asas al-Quran", "text-q"], ["V", "Nilai", "text-v"], ["K", "Ilmu", "text-k"], ["I", "Inovasi → Tindakan", "text-i"]].map(([l, n, c]) => (
            <li key={l} className="flex items-baseline gap-3"><span className={`font-display text-2xl ${c}`}>{l}</span><span className="text-mute">{n}</span></li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="pb-16" data-testid="home-steps">
    <div className="eyebrow mb-5">Cara HTE membantu</div>
    <div className="grid md:grid-cols-3 gap-4">
      {STEPS.map(([Icon, t, d]) => (
        <div key={t} className="surface rounded-2xl p-6 border border-line">
          <Icon size={20} className="text-q" />
          <h3 className="font-display text-xl text-ink mt-4">{t}</h3>
          <p className="text-sm text-mute mt-2 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Methods = () => (
  <section className="pb-16" data-testid="home-methods">
    <div className="eyebrow mb-2">Metodologi di Sebalik HTE</div>
    <p className="text-sm text-mute max-w-2xl mb-5">Empat kaedah ini ialah asas pemikiran bagi cara HTE mengkaji, mereka bentuk dan menyampaikan latihan. Ia bukan modul berasingan; prinsipnya diterapkan dalam aliran kerja HTE, dan kaedah penuhnya dipelajari bersama trainer ATAS.</p>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {METHODS.map(([k, n, d]) => (
        <div key={k} className="rounded-2xl p-5 border border-line" data-testid={`method-card-${k.toLowerCase()}`}>
          <div className="font-display text-2xl text-ink">{k}</div>
          <div className="text-xs text-mute font-mono-data mt-1">{n}</div>
          <p className="text-sm text-ink/90 mt-3 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Sample = ({ ayat }) => (
  <section className="pb-16" data-testid="home-sample">
    <div className="eyebrow mb-5">Contoh hasil HTE</div>
    <div className="surface rounded-2xl border border-line p-6 sm:p-8 grid lg:grid-cols-2 gap-8">
      <div>
        <div className="text-xs text-mute font-mono-data">Program contoh · Bengkel 3 jam · Penyelia barisan hadapan</div>
        <h3 className="font-display text-2xl text-ink mt-2">Amanah dalam Kepimpinan Pasukan</h3>
        <div className="mt-5 rounded-xl border border-line p-4">
          <div className="text-xs font-semibold text-i">Dalil disahkan · Surah An-Nisa' 4:58</div>
          {ayat ? <>
            <p className="arabic-sm text-lg text-ink text-right mt-2" lang="ar" dir="rtl" data-testid="home-sample-arab">{ayat.ar}</p>
            <p className="text-sm text-ink/90 mt-2 leading-relaxed" data-testid="home-sample-terjemahan">{ayat.ms}</p>
            <p className="text-[11px] text-mute font-mono-data mt-2">Terjemahan: Abdullah Muhammad Basmeih</p>
          </> : <p className="text-sm text-mute mt-2">Memuatkan ayat…</p>}
        </div>
      </div>
      <div className="space-y-4 text-sm">
        <div className="text-xs font-semibold text-q">Contoh struktur yang dibina (cadangan AI, untuk disemak trainer)</div>
        <div><div className="eyebrow mb-1">Objektif pembelajaran</div><ul className="list-disc pl-5 space-y-0.5 text-ink"><li>Menjelaskan maksud amanah dalam tugas harian penyelia</li><li>Mengenal pasti satu amalan pasukan yang tidak menunaikan amanah</li><li>Merangka satu tindakan pembetulan untuk minggu hadapan</li></ul></div>
        <div><div className="eyebrow mb-1">Rangka sesi</div><ul className="space-y-0.5 text-ink"><li><span className="text-q font-mono-data">Q</span> Tadabbur 4:58 dan ayat berkaitan</li><li><span className="text-v font-mono-data">V</span> Nilai: amanah, adil, tanggungjawab</li><li><span className="text-k font-mono-data">K</span> Kemahiran: agihan tugas dan maklum balas</li><li><span className="text-i font-mono-data">I</span> Pelan tindakan pasukan 7 hari</li></ul></div>
        <div><div className="eyebrow mb-1">Bahan yang boleh disalin</div><p className="text-mute">Rangka Kuliah · Lesson Plan · Soalan Refleksi · Aktiviti Kumpulan · Rangka Slaid · Tindakan Peserta</p></div>
      </div>
    </div>
  </section>
);

const Pricing = ({ p }) => (
  <section className="pb-16" data-testid="home-pricing">
    <div className="eyebrow mb-5">Harga</div>
    <div className="grid md:grid-cols-4 gap-4">
      <div className="rounded-2xl border border-line p-5"><div className="font-display text-xl text-ink">Percuma</div><div className="text-2xl mt-2 text-ink">{p ? p.percuma : "—"} <span className="text-sm text-mute">carian / bulan</span></div></div>
      <div className="rounded-2xl border border-line p-5"><div className="font-display text-xl text-ink">Per Use</div><div className="text-2xl mt-2 text-ink">{p ? rm(p.payg_sen) : "—"} <span className="text-sm text-mute">/ carian</span></div></div>
      {p && Object.entries(p.pelan).map(([k, v]) => (
        <div key={k} className="rounded-2xl border border-line p-5" data-testid={`home-plan-${k}`}><div className="font-display text-xl text-ink">{v.nama}</div><div className="text-2xl mt-2 text-ink">{rm(v.harga_sen)} <span className="text-sm text-mute">/ {v.hari} hari</span></div></div>
      ))}
    </div>
    <p className="text-sm text-mute mt-4 max-w-3xl" data-testid="home-pricing-note">
      <strong className="text-ink">Dikira 1 carian:</strong> setiap penjanaan AI — "Jana HTE" atau "Bina Kerangka Program" (termasuk versi baharu).{" "}
      <strong className="text-ink">Percuma:</strong> mencari calon ayat, melihat, menyunting, menyalin, menyimpan, membuka semula program dan mencatat AAR.
    </p>
  </section>
);

export default function Home() {
  const [ayat, setAyat] = useState(null);
  const [plans, setPlans] = useState(null);
  useEffect(() => {
    axios.get(`${API}/quran/ayat`, { params: { surah: 4, from: 58, to: 58 } }).then((r) => setAyat((r.data.ayat || r.data)[0])).catch(() => {});
    axios.get(`${API}/billing/plans`).then((r) => setPlans(r.data)).catch(() => {});
  }, []);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8">
      <Hero />
      <div className="pb-12 max-w-3xl"><TipBanner testid="home-tip" /></div>
      <Steps />
      <Methods />
      <Sample ayat={ayat} />
      <Pricing p={plans} />
      <section className="pb-20 text-center" data-testid="home-final-cta">
        <h2 className="font-display text-3xl sm:text-4xl text-ink">Mulakan Program Anda dengan HTE</h2>
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <Link to="/program" className={btnMain} data-testid="home-final-cta-program">Saya Ada Program <ArrowRight size={16} /></Link>
          <Link to="/hte" className={btnAlt} data-testid="home-final-cta-kaji">Kaji Ayat al-Quran</Link>
        </div>
      </section>
    </div>
  );
}
